import { useEffect, useMemo, useRef, useState, type JSX, type KeyboardEvent } from 'react'
import { useNavigate } from 'react-router'
import { Search } from 'lucide-react'

export interface NavSearchItem {
  /** Route the leaf navigates to. */
  to: string
  /** Leaf name as it reads in the side panel. */
  label: string
  /** Owning side-panel section, shown as context in the result row. */
  section: string
}

interface NavSearchProps {
  /** Every side-panel leaf the current user may reach. */
  items: readonly NavSearchItem[]
  placeholder?: string
  /** Accessible name for the input. */
  label?: string
}

const MAX_RESULTS = 8
const DEFAULT_PLACEHOLDER = 'Search menu...'
const DEFAULT_LABEL = 'Search navigation'

function matchItems(items: readonly NavSearchItem[], query: string): NavSearchItem[] {
  const q = query.trim().toLowerCase()
  if (q.length === 0) return []
  // Leaf-name matches rank above section-name matches so typing an item name
  // never buries it under every sibling of a same-named section.
  const leafHits: NavSearchItem[] = []
  const sectionHits: NavSearchItem[] = []
  for (const item of items) {
    if (item.label.toLowerCase().includes(q)) leafHits.push(item)
    else if (item.section.toLowerCase().includes(q)) sectionHits.push(item)
  }
  return [...leafHits, ...sectionHits].slice(0, MAX_RESULTS)
}

/**
 * Inline header search over the side-panel (sub-navigation) names. Replaces the
 * former button that opened the global Cmd+K palette: this searches menu entries
 * only and navigates straight to the matched route.
 *
 * Results are filtered from the already permission-filtered leaf list passed in,
 * so a user can never search their way to a page they cannot open.
 */
export default function NavSearch({
  items,
  placeholder = DEFAULT_PLACEHOLDER,
  label = DEFAULT_LABEL,
}: NavSearchProps): JSX.Element {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)

  const results = useMemo(() => matchItems(items, query), [items, query])

  useEffect(() => {
    setActiveIndex(0)
  }, [query])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent): void {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const select = (item: NavSearchItem): void => {
    navigate(item.to)
    setQuery('')
    setOpen(false)
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Escape') {
      setOpen(false)
      return
    }
    if (results.length === 0) return
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveIndex((i) => Math.min(i + 1, results.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex((i) => Math.max(i - 1, 0))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const target = results[activeIndex]
      if (target) select(target)
    }
  }

  const showPanel = open && query.trim().length > 0

  return (
    <div className="relative" ref={containerRef}>
      <div className="flex items-center gap-2 h-9 px-3 rounded-lg bg-gray-50 border border-gray-100 focus-within:bg-white focus-within:border-gray-200 transition-all">
        <Search className="h-4 w-4 text-gray-400 shrink-0" />
        <input
          type="text"
          value={query}
          aria-label={label}
          placeholder={placeholder}
          role="combobox"
          aria-expanded={showPanel}
          aria-controls="nav-search-results"
          aria-autocomplete="list"
          className="w-44 lg:w-56 bg-transparent text-[13px] text-gray-700 placeholder:text-gray-400 outline-none"
          onChange={(e) => {
            setQuery(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
        />
      </div>

      {showPanel && (
        <div
          id="nav-search-results"
          role="listbox"
          aria-label="Navigation search results"
          className="absolute left-0 top-11 w-80 max-h-80 overflow-y-auto bg-white rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] border border-gray-100 z-50 py-1 nav-scroll"
        >
          {results.length === 0 && (
            <p className="px-4 py-3 text-[13px] text-gray-400">No matching menu items</p>
          )}
          {results.map((item, index) => (
            <button
              key={item.to}
              type="button"
              role="option"
              aria-selected={index === activeIndex}
              // mousedown fires before the input's blur, so the click is not
              // lost to the outside-click handler closing the panel.
              onMouseDown={(e) => {
                e.preventDefault()
                select(item)
              }}
              onMouseEnter={() => setActiveIndex(index)}
              className={`w-full text-left px-4 py-2 transition-colors ${
                index === activeIndex ? 'bg-[#3454cf]/6' : 'hover:bg-gray-50'
              }`}
            >
              <span className="block text-[13px] font-medium text-gray-800 truncate">
                {item.label}
              </span>
              <span className="block text-[11px] text-gray-400 truncate">{item.section}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
